import dayjs from "dayjs";
import { GetSchedule, GetSlot, PatchSchedule } from "@/services/fhir";
import { TELECONSULTORIA_SERVICE_CATEGORY_TOKEN } from "../../constants";
import {
  schedulesFromBundle,
  setTeleconsultoriaScheduleActive,
} from "../../services/teleconsultoriaScheduleService";
import {
  actorByType,
  isSamePractitionerReference,
  isTeleconsultoriaAgendaSchedule,
} from "../../utils/actors";
import { sortAgendaSummaries, toAgendaSummary } from "../transformers/agendaSummary";
import type { AgendaEditValues, TeleconsultoriaAgendaSummary } from "../types";

const SCHEDULE_SEARCH_COUNT = 100;
/** Janela da contagem de horários livres exibida no card. */
export const FREE_SLOTS_DAYS_AHEAD = 7;

/**
 * Agendas de teleconsultoria COM horários da organização (ativas e inativas).
 * Com `practitionerReference`, só as daquele profissional (comparado pelo CPF).
 */
export async function listTeleconsultoriaAgendas(params: {
  organizationToken: string;
  practitionerReference?: string;
}): Promise<TeleconsultoriaAgendaSummary[]> {
  if (!params.organizationToken) return [];
  const bundle = await GetSchedule({
    organization: params.organizationToken,
    serviceCategory: TELECONSULTORIA_SERVICE_CATEGORY_TOKEN,
    count: SCHEDULE_SEARCH_COUNT,
    ...(params.practitionerReference && { practitioner: params.practitionerReference }),
  });
  const summaries = schedulesFromBundle(bundle)
    .filter(isTeleconsultoriaAgendaSchedule)
    .filter(
      (schedule) =>
        !params.practitionerReference ||
        isSamePractitionerReference(
          actorByType(schedule.actor, "Practitioner")?.reference,
          params.practitionerReference,
        ),
    )
    .map(toAgendaSummary)
    .filter((summary): summary is TeleconsultoriaAgendaSummary => Boolean(summary));
  return sortAgendaSummaries(summaries);
}

/** Horários livres nos próximos dias (só a contagem, `_summary=count`). */
export async function countUpcomingFreeSlots(scheduleId: string): Promise<number | undefined> {
  try {
    const bundle: fhir5.Bundle | undefined = await GetSlot({
      schedule: scheduleId,
      status: ["free"],
      startDate: dayjs().toISOString(),
      endDate: dayjs().add(FREE_SLOTS_DAYS_AHEAD, "day").format("YYYY-MM-DD"),
      summary: true,
    });
    return typeof bundle?.total === "number" ? bundle.total : undefined;
  } catch (error) {
    console.error("countUpcomingFreeSlots", scheduleId, error);
    return undefined;
  }
}

/** Desativar/reativar = PATCH `active` (desativar não apaga: o histórico fica). */
export function setAgendaActive(params: { schedule: fhir5.Schedule; isActive: boolean }) {
  return setTeleconsultoriaScheduleActive(params.schedule, params.isActive);
}

/** Mesmos campos e PATCH do EditScheduleDialog (nome e período), só o que mudou. */
export async function updateAgenda(params: {
  summary: TeleconsultoriaAgendaSummary;
  values: AgendaEditValues;
}): Promise<void> {
  const { summary, values } = params;
  const operations = [
    values.name.trim() !== summary.name && {
      op: "replace",
      path: "/name",
      value: values.name.trim(),
    },
    !dayjs(values.periodStart).isSame(dayjs(summary.periodStart), "day") && {
      op: "replace",
      path: "/planningHorizon/start",
      value: dayjs(values.periodStart).startOf("day").toISOString(),
    },
    !dayjs(values.periodEnd).isSame(dayjs(summary.periodEnd), "day") && {
      op: "replace",
      path: "/planningHorizon/end",
      value: dayjs(values.periodEnd).endOf("day").toISOString(),
    },
  ].filter(Boolean);
  if (!operations.length) return;
  await PatchSchedule(summary.id, operations);
}
